import Close from '@mui/icons-material/Close';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Data } from '../properties-state';

const PREFIX = 'PropertiesCard';

const classes = {
  propertiesContent: `${PREFIX}-propertiesContent`,
  pathContent: `${PREFIX}-pathContent`,
  headerContent: `${PREFIX}-headerContent`,
  metricsContent: `${PREFIX}-metricsContent`,
  paper: `${PREFIX}-paper`,
  closeIcon: `${PREFIX}-closeIcon`
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`& .${classes.propertiesContent}`]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(0),
    marginTop: -18,
    position: 'relative',
    width: `calc(100% - ${theme.spacing(3)})`,
    flex: '1 1 auto'
  },

  [`& .${classes.pathContent}`]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(0),
    flex: '0 1 auto'
  },

  [`& .${classes.headerContent}`]: {
    marginTop: -6,
    flex: '0 1 auto'
  },

  [`& .${classes.metricsContent}`]: {
    marginTop: 10
  },

  [`&.${classes.paper}`]: {
    backgroundColor: theme.palette.background.paper,
    height: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    '&::-webkit-scrollbar': {
      width: '7px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '3px'
    },
    scrollbarColor: '#888 #f1f1f1',
    scrollbarWidth: 'thin'
  },

  [`& .${classes.closeIcon}`]: {
    verticalAlign: 'middle',
    cursor: 'pointer',
    float: 'right'
  }
}));

interface PropertiesCardProps {
  meaData?: Data;
  onClose: () => void;
  name?: string;
  description?: string;
  path?: string;
  children?: any;
  fullscreen?: boolean;
  toggleFullscreen?: any;
}

const PropertiesCard = ({
  name,
  description,
  path = '',
  onClose,
  children,
  fullscreen = false,
  toggleFullscreen
}: PropertiesCardProps) => {
  return (
    <StyledCard className={classes.paper} data-testid="expansible-properties">
      <CardContent className={classes.pathContent}>
        <Tooltip title={fullscreen ? 'Exit fullscreen' : 'Close'}>
          {fullscreen ? (
            <FullscreenExit
              data-testid="fullscreen-exit-icon"
              className={classes.closeIcon}
              onClick={toggleFullscreen}
            />
          ) : (
            <Close data-testid="close-icon" className={classes.closeIcon} onClick={onClose} />
          )}
        </Tooltip>
        <Typography variant="caption" data-testid="properties-path">
          {path}
        </Typography>
      </CardContent>
      <CardHeader
        title={name}
        className={classes.headerContent}
        titleTypographyProps={{ align: 'left', variant: 'subtitle1' }}
        data-testid="properties-title"
        subheader={fullscreen ? '' : description || ''}
        subheaderTypographyProps={{
          'data-testid': 'properties-description',
          align: 'left',
          variant: 'body2'
        }}
      />
      <CardContent className={classes.propertiesContent}>{children}</CardContent>
    </StyledCard>
  );
};

export default PropertiesCard;
